import { ApprovalPicker, NO_APPROVAL } from "./Approval";
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { liveMilestones, liveTasks, useProjectStore, type ProjectData } from "@/lib/store";
import { TASK_STATUSES } from "@/lib/tasks";
import { toast } from "@/lib/toast";
import { Picker, PropButton } from "./Picker";
import { Avatar } from "./PropertyIcons";
import { TaskStatusIcon } from "./TaskProps";
import { MarkdownEditor } from "./Markdown";

export type TaskDefaults = { status?: string; parent_id?: string; milestone_id?: string; lead_id?: string | null };

export function TaskCreateDialog({ d, open, onOpenChange, defaults }: { d: ProjectData; open: boolean; onOpenChange: (o: boolean) => void; defaults?: TaskDefaults }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {open && <Form d={d} onOpenChange={onOpenChange} defaults={defaults} />}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Form({ d, onOpenChange, defaults }: { d: ProjectData; onOpenChange: (o: boolean) => void; defaults?: TaskDefaults }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(defaults?.status ?? "backlog");
  const [lead, setLead] = useState<string | null>(defaults?.lead_id ?? null);
  const [milestone, setMilestone] = useState<string | null>(defaults?.milestone_id ?? null);
  const [parent, setParent] = useState<string | null>(defaults?.parent_id ?? null);
  const [start, setStart] = useState("");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [approval, setApproval] = useState(NO_APPROVAL);
  const [editorBusy, setEditorBusy] = useState(false);
  const leadUser = lead ? d.users[lead] : undefined;

  const submit = async () => {
    if (!name.trim() || busy || editorBusy) return;
    setBusy(true);
    try {
      const task = unwrap(
        await api.POST("/api/workspaces/{slug}/projects/{key}/tasks", {
          params: { path: { slug: d.slug, key: d.project.key } },
          body: { ...approval, name: name.trim(), summary: summary || null, description: description || null, status, lead_id: lead, milestone_id: milestone, parent_id: parent, start_date: start || null, target_date: target || null },
        }),
      );
      useProjectStore.getState().applyTask(task);
      toast(t("task.created", { name: task.name }), { action: { label: t("common.open"), run: () => void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: task.id } }) } });
      onOpenChange(false);
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Content
      className="fixed left-1/2 top-[14%] z-50 w-[680px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-xl border border-border bg-elevated shadow-2xl"
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault();
          void submit();
        }
      }}
    >
      <div className="flex items-center gap-2 px-4 pt-3 text-[12px] text-muted">
        <span className="rounded border border-border px-1.5 py-px">{d.project.key}</span>
        <span>›</span>
        <Dialog.Title className="font-medium">{parent ? t("task.newSubTask") : t("task.newTask")}</Dialog.Title>
        <Dialog.Close className="ml-auto rounded p-1 hover:bg-hover"><X className="h-4 w-4" /></Dialog.Close>
      </div>
      <div className="px-4 pt-2">
        <input autoFocus className="w-full bg-transparent text-[16px] font-medium text-fg outline-none placeholder:text-faint" placeholder={t("task.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void submit(); } }} />
        <input className="mt-1 w-full bg-transparent text-[13px] text-fg outline-none placeholder:text-faint" placeholder={t("task.summaryPlaceholder")} value={summary} onChange={(e) => setSummary(e.target.value)} />
        <div className="mt-2"><MarkdownEditor onBusyChange={setEditorBusy} projectId={d.project.id} value={description} onChange={setDescription} onSave={() => void submit()} disabled={busy} placeholder={t("task.descriptionPlaceholder")} minRows={3} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-3">
          <ApprovalPicker d={d} value={approval} onChange={(config) => { setApproval(config); if (config.approval_required && status === "completed") setStatus("done"); }} disabled={busy} />
        <Picker value={status} items={TASK_STATUSES.filter((s) => !approval.approval_required || s !== "completed").map((s) => ({ id: s, label: t(`taskStatus.${s}`), icon: <TaskStatusIcon status={s} /> }))} onSelect={setStatus}
          trigger={<PropButton className="border-border-strong"><TaskStatusIcon status={status} /> {t(`taskStatus.${status}`)}</PropButton>} />
        <Picker value={lead ?? "none"} items={[{ id: "none", label: t("task.noLead"), icon: <Avatar size={16} /> }, ...Object.values(d.users).map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} /> }))]} onSelect={(v) => setLead(v === "none" ? null : v)}
          trigger={<PropButton className="border-border-strong"><Avatar user={leadUser} size={16} /> {leadUser?.name ?? t("task.lead")}</PropButton>} />
        <Picker value={milestone ?? "none"} items={[{ id: "none", label: t("task.noMilestone") }, ...liveMilestones(d).map((m) => ({ id: m.id, label: m.name, hint: m.target_date }))]} onSelect={(v) => setMilestone(v === "none" ? null : v)}
          trigger={<PropButton className="border-border-strong">{milestone ? d.milestones[milestone]?.name : t("nav.milestone")}</PropButton>} />
        <Picker value={parent ?? "none"} items={[{ id: "none", label: t("task.noParent") }, ...liveTasks(d).map((x) => ({ id: x.id, label: x.name, icon: <TaskStatusIcon status={x.status} className="h-3 w-3" /> }))]} onSelect={(v) => setParent(v === "none" ? null : v)}
          trigger={<PropButton className="border-border-strong">{parent ? `↳ ${d.tasks[parent]?.name}` : t("task.parent")}</PropButton>} />
        <label className="inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-md border border-border-strong px-1.5 text-[12.5px] text-muted hover:bg-hover">{start || t("task.startDate")}<input type="date" className="sr-only" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-md border border-border-strong px-1.5 text-[12.5px] text-muted hover:bg-hover">{target || t("task.targetDate")}<input type="date" className="sr-only" value={target} onChange={(e) => setTarget(e.target.value)} /></label>
      </div>
      <div className="flex items-center justify-end border-t border-border px-4 py-2.5">
        <button className="btn btn-primary h-8" disabled={!name.trim() || busy || editorBusy} onClick={() => void submit()}>
          {t("task.create")} <kbd className="kbd ml-1">⌘↵</kbd>
        </button>
      </div>
    </Dialog.Content>
  );
}
